import {
  Box,
  HStack,
  Icon,
  IconButton,
  Stack,
  StackProps,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import {
  ArrowUpIcon,
  BicycleIcon,
  DeleteIcon,
} from "../../../../shared/views/icons";
import {
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  Score,
} from "@keita/lanepatrol-sdk";
import { LightSource } from "./LightSource";
import {
  backgroundGradient,
  cycleRedColor,
  multiUsePathGradient,
  onRoadBicycleLaneGradient,
  roadLaneColor,
  roadShoulderColor,
  segregationColor,
  sidewalkGradient,
} from "./constants";
import { CrossSectionAdjacentFeatures } from "./CrossSectionAdjacentFeatures";
import { CreateOrUpdateHlaLane } from "../../../models/HlaLane";
import { HlaScoreEntryView } from "../../HlaScoreView";
import { useStores } from "../../../../shared/stores/RootStore";

const CrossSectionViewBase = ({
  lit,
  lane,
  canEdit,
  isSelected,
  readOnly,
  score,
  onSelect,
  onDelete,
}: {
  readOnly?: boolean;
  lit: boolean;
  lane: CreateOrUpdateHlaLane;
  canEdit: boolean;
  isSelected: boolean;
  score?: Score | null;
  onSelect?: () => void;
  onDelete?: () => void;
}) => {
  const { surveyStore } = useStores();

  const showTwoLanes =
    lane.flowDirection === FlowDirectionType.TwoWay &&
    lane.facilityType !== FacilityType.Sidewalk;

  const widthPerDirection =
    facilityWidthMultiplier(lane.facilityWidthPerDirection) * 28;

  const mainLaneProps: StackProps = {};

  if (lane.facilityType === FacilityType.OffRoadBicyclePath) {
    mainLaneProps.bgColor = cycleRedColor;
  } else if (lane.facilityType === FacilityType.OnRoadBicycleLane) {
    mainLaneProps.bgImage = onRoadBicycleLaneGradient;
  } else if (lane.facilityType === FacilityType.MixedTrafficRoadLane) {
    mainLaneProps.bgColor = roadLaneColor;
  } else if (lane.facilityType === FacilityType.RoadShoulder) {
    mainLaneProps.bgColor = roadShoulderColor;
    mainLaneProps.borderLeft = `2px dashed white`;
    mainLaneProps.borderRight = `2px dashed white`;
  } else if (lane.facilityType === FacilityType.Sidewalk) {
    mainLaneProps.bgSize = "10px 10px";
    mainLaneProps.bgImage = sidewalkGradient;
  } else if (lane.facilityType === FacilityType.MultiUsePath) {
    mainLaneProps.bgSize = "10px 10px";
    mainLaneProps.bgImage = multiUsePathGradient;
  }

  if (lane.lightSegregation) {
    mainLaneProps.borderLeft = `4px dashed ${segregationColor}`;
    mainLaneProps.borderRight = `4px dashed ${segregationColor}`;
  }

  return (
    <VStack>
      {score?.global && <HlaScoreEntryView score={score?.global} />}

      <HStack
        borderWidth={"3px"}
        cursor={isSelected ? "auto" : "pointer"}
        _hover={{ bgColor: "primary.100" }}
        borderColor={isSelected ? "primary.500" : "transparent"}
        position="relative"
        flexShrink={0}
        overflow={"hidden"}
        borderRadius={"8px"}
        onClick={onSelect}
      >
        <HStack
          bgColor="background-color: #e5e5f7"
          bgImage={backgroundGradient}
          backgroundPosition="0 0, 10px 10px"
          bgSize="20px 20px"
          justifyContent={"center"}
          gap={0}
          alignItems="stretch"
          alignSelf="center"
          h={"80px"}
          rotate={lane.frontFacing ? `0deg` : `180deg`}
          overflow={"hidden"}
          borderRadius={"4px"}
        >
          <CrossSectionAdjacentFeatures lane={lane} side="left" />

          <HStack
            gap={1}
            alignItems={"center"}
            transitionDuration={"0.2s"}
            bgColor={"#e7c73dff"}
            {...mainLaneProps}
            flexDir={surveyStore.rightHandTraffic ? "row" : "row-reverse"}
          >
            {showTwoLanes && <Box w={`${widthPerDirection}px`} />}

            {showTwoLanes && <Box borderLeft={"4px dashed white"} h={"full"} />}

            <Stack
              w={`${widthPerDirection}px`}
              h={"full"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={0}
              transitionDuration={"0.2s"}
            >
              <Icon color={"white"} as={ArrowUpIcon} />
              <Icon color={"white"} as={BicycleIcon} />
            </Stack>
          </HStack>

          <CrossSectionAdjacentFeatures lane={lane} side="right" />
        </HStack>
        {lit && <LightSource />}
      </HStack>

      {!readOnly && (
        <Box h={"40px"}>
          {isSelected && (
            <IconButton
              size={"xs"}
              disabled={!canEdit}
              variant={"surface"}
              colorPalette={"primary"}
              onClick={onDelete}
            >
              <Icon as={DeleteIcon} />
            </IconButton>
          )}
        </Box>
      )}
    </VStack>
  );
};

export const CrossSectionView = observer(CrossSectionViewBase);

const facilityWidthMultiplier = (option: FacilityWidth | null) => {
  switch (option) {
    case FacilityWidth.VeryNarrow:
      return 1;
    case null:
    case FacilityWidth.Narrow:
      return 1.8;
    case FacilityWidth.Wide:
      return 2.5;
  }
};
